<template>
  <div class="booking-pages">
    <transition name="fade-slide">
      <div v-if="isLoading" class="global-loader">
        <div class="loader-spinner"></div>
        <p style="color: black;">Loading...</p>
      </div>
    </transition>
    <!-- === KPI SUMMARY CARDS === -->
    <div class="kpi-row fade-in">
      <div class="kpi-card" v-for="(stat, i) in stats" :key="i">
        <div>
          <h4>{{ stat.title }}</h4>
          <div class="value">{{ stat.value }}</div>
        </div>
        <i :class="stat.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- === HEADER FILTERS + ADD BUTTON === -->
    <div class="grid-header fade-in">
      <div class="filters">
        <div class="search-box">
          <i class="bx bx-search"></i>
          <input v-model="searchQuery" type="text" placeholder="Search pages..." @input="applyFilters" />
        </div>

        <select v-model="selectedStatus" @change="applyFilters">
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Draft">Draft</option>
        </select>
      </div>

      <button class="btn-primary" @click="addNewPage">
        <i class="bx bx-plus"></i> New Page
      </button>
    </div>

    <!-- === BOOKING PAGE CARDS === -->
    <div v-if="!isLoading && pages.length > 0" class="website-grid fade-in">
      <div class="website-card" v-for="(page, i) in filteredPages" :key="i">
        <div class="website-card-header">
          <h4><i class="bx bx-calendar-event"></i> {{ page.booking_page_title }}</h4>

          <div class="card-menu">
            <i class="bx bx-dots-vertical-rounded"></i>
            <div class="card-menu-dropdown">
              <button title="Copy Link" @click="copyLink(page.booking_page_url)">
                <i class="bx bx-copy"></i> Copy Link
              </button>
              <button title="Copy Link" @click="copyLink1(page.booking_page_url)">
                <i class="bx bx-copy"></i> Copy Embed Link
              </button>
              <button title="Edit Page" @click="editPage(page)">
                <i class="bx bx-edit"></i> Edit
              </button>
              <button title="Delete Page" @click="confirmDelete(page)">
                <i class="bx bx-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>

        <div class="website-card-body">
          <div class="page-meta">
            <div><i class="bx bx-link"></i> {{ page.booking_page_url }}</div>
            <div><i class="bx bx-user"></i> Layout: {{ page.booking_page_layout_type }}</div>
            <div><i class="bx bx-refresh"></i> Recurring: {{ page.booking_page_recurring }}</div>
          </div>

          <div class="page-stats">
            <span class="status" :class="page.booking_page_status.toLowerCase()">
              <i v-if="page.booking_page_status === 'Active'" class="bx bx-check-circle"></i>
              <i v-else-if="page.booking_page_status === 'Pending'" class="bx bx-time"></i>
              <i v-else class="bx bx-pencil"></i>
              {{ page.booking_page_status }}
            </span>

            <span class="created-at">
              <i class="bx bx-calendar"></i>
              {{ page.booking_page_datetime }}
            </span>
          </div>
        </div>

        <div class="website-card-footer">
          <span class="website-domain">{{ page.booking_page_url }}</span>
          <button class="btn-copy" @click="copyLink(page.booking_page_url)">
            <i class="bx bx-copy"></i> Copy Link
          </button>
        </div>
      </div>
    </div>

    
    <div v-else-if="!isLoading && pages.length === 0" class="empty-state">
        <i class="bx bx-notepad"></i>
        <h3>No booking pages Found</h3>
        <p>You haven’t created any booking page yet. Let’s set up your first one.</p>
        <button
          class="btn-primary"
          @click="addNewPage"
          style="padding: 8px 14px; font-size: 13px; border-radius: 6px;"
        >
          <i class="bx bx-plus" style="font-size: 14px; color: white; margin-top: .7rem;"></i> Create New Page
        </button>
      </div>


    <!-- === LOADER OVERLAY === -->
    <div v-if="isLoading" class="loader-overlay">
      <div class="spinner"></div>
      <p>Loading booking pages...</p>
    </div>

    <!-- === PAGINATION === -->
    <div v-if="!isLoading && pages.length > 0" class="pagination fade-in">
      <span>Showing {{ startIndex + 1 }}–{{ endIndex }} of {{ filteredPages.length }} pages</span>
      <div class="pagination-buttons">
        <button :disabled="currentPage === 1" @click="prevPage"><i class="bx bx-chevron-left"></i></button>
        <button class="active">{{ currentPage }}</button>
        <button :disabled="endIndex >= filteredPages.length" @click="nextPage"><i class="bx bx-chevron-right"></i></button>
      </div>
    </div>

    <br><br>
  </div>
</template>

<script scoped>
import { getBookPages, getBookPageReport, deleteBookPage } from "@/apis/bookpageapis";
import { mainBaseUrl } from "@/apis/appconst";
import { useToast } from "vue-toastification";

export default {
  name: "BookingPages",
  data() {
    return {
      isLoading: false,
      pages: [],
      searchQuery: "",
      selectedStatus: "",
      currentPage: 1,
      perPage: 6,
      stats: [
        { title: "All Pages", value: 0, icon: "bx bx-globe" },
        { title: "Bookings", value: 0, icon: "bx bx-calendar" },
        { title: "Pending", value: 0, icon: "bx bx-time" },
        { title: "Completed", value: 0, icon: "bx bx-check-circle" },
      ],
    };
  },
  computed: {
    filteredPages() {
      let data = this.pages;
      if (this.searchQuery) {
        data = data.filter((p) =>
          p.booking_page_title.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
      if (this.selectedStatus) {
        data = data.filter(
          (p) => p.booking_page_status === this.selectedStatus
        );
      }
      return data;
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.perPage, this.filteredPages.length);
    },
    paginatedPages() {
      return this.filteredPages.slice(this.startIndex, this.endIndex);
    },
  },
  mounted() {
    this.fetchPages();
    this.fetchReport();
  },
  methods: {
    async fetchPages() {
      // const toast = useToast();
      try {
        this.isLoading = true;
        const ldata = JSON.parse(sessionStorage.getItem("ldata"));
        const res = await getBookPages(
          ldata.user_id,
          this.currentPage - 1,
          this.selectedStatus,
          "",
          this.searchQuery
        );
        if (res.status && res.records?.length) {
          this.pages = res.records;
        } else {
          this.pages = [];
          // toast.success(res.success || "No pages found");
        }
      } catch (error) {
        console.error("fetchPages error:", error);
        // toast.error("Failed to load pages");
      } finally {
        this.isLoading = false;
      }
    },

    async fetchReport() {
      try {
        const ldata = JSON.parse(sessionStorage.getItem("ldata"));
        const res = await getBookPageReport(ldata.user_id);
        if (res.status) {
          this.stats = [
            { title: "All Pages", value: res.all_pages, icon: "bx bx-globe" },
            { title: "Bookings", value: res.bookings, icon: "bx bx-calendar" },
            { title: "Pending", value: res.pending, icon: "bx bx-time" },
            { title: "Completed", value: res.completed, icon: "bx bx-check-circle" },
          ];
        }
      } catch (e) {
        console.error("fetchReport error:", e);
      }
    },

    applyFilters() {
      this.currentPage = 1;
      this.fetchPages();
      this.fetchReport();
    },

    nextPage() {
      if (this.endIndex < this.filteredPages.length) {
        this.currentPage++;
        this.fetchPages();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchPages();
      }
    },

    async confirmDelete(page) {
      const toast = useToast();
      if (!confirm(`Delete page "${page.booking_page_title}"?`)) return;
      try {
        this.isLoading = true;
        const res = await deleteBookPage(page.booking_page_id);
        if (res.status) {
          toast.success("Page deleted successfully");
          this.fetchPages();
          this.fetchReport();
        } else toast.error(res.success || "Failed to delete");
      } catch (e) {
        console.error("deletePage error:", e);
        toast.error("Error deleting page");
      } finally {
        this.isLoading = false;
      }
    },

    copyLink(link) {
      navigator.clipboard.writeText(mainBaseUrl+"appointment/"+link);
      const toast = useToast();
      toast.success("Copied link: " + link);
    },
    copyLink1(link) {
      navigator.clipboard.writeText(mainBaseUrl+"appointment/embed/"+link);
      const toast = useToast();
      toast.success("Copied link: " + link);
    },
    editPage(page) {
      sessionStorage.setItem("booking_page_data",JSON.stringify(page));
      this.$router.push({
        path: "/add_booking_page"
      });
    },

    addNewPage() {
      sessionStorage.removeItem("booking_page_data");
      this.$router.push("/add_booking_page");
    },
  },
};
</script>


<style scoped>
/* === Fade In Animation === */
.fade-in {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Filters === */
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.filters select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
}

/* === Page Meta Info === */
.page-meta {
  display: flex;
  justify-content: space-between;
  color: var(--muted);
  font-size: 13px;
  flex-wrap: wrap;
  gap: 8px;
}
.page-meta i {
  color: var(--primary);
  margin-right: 4px;
}

/* === Page Status / Date === */
.page-stats {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.status {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}
.status.active {
  background: #dbeafe;
  color: #1e3a8a;
}
.status.pending {
  background: #fef3c7;
  color: #92400e;
}
.status.draft {
  background: #f3f4f6;
  color: #4b5563;
}
.created-at {
  color: var(--muted);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* === Copy Button === */
.btn-copy {
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
}
.btn-copy:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}

/* === Dropdown === */
.card-menu {
  position: relative;
}
.card-menu i {
  cursor: pointer;
  font-size: 1.2rem;
}
.card-menu-dropdown {
  width: 10rem;
  position: absolute;
  right: 0;
  top: 1.5rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: none;
  flex-direction: column;
  z-index: 10;
}
.card-menu:hover .card-menu-dropdown {
  display: flex;
}
.card-menu-dropdown button {
  border: none;
  background: none;
  text-align: left;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.25s ease;
}
.card-menu-dropdown button:hover {
  background: #f3f4f6;
}

/* === Pagination === */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pagination-buttons button {
  border: none;
  background: #f3f4f6;
  color: var(--text);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
}
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}
/* ==========================================================
   📱 MOBILE + TABLET RESPONSIVENESS FIXES
   ========================================================== */

/* === KPI ROW === */
@media (max-width: 992px) {
  .kpi-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .kpi-card {
    flex: 1 1 calc(50% - 16px);
    min-width: 220px;
  }
}

@media (max-width: 600px) {
  .kpi-card {
    flex: 1 1 100%;
  }
}

/* === FILTERS + HEADER === */
@media (max-width: 768px) {
  .grid-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .filters {
    width: 100%;
    justify-content: space-between;
  }

  .filters select,
  .filters .search-box {
    flex: 1;
    min-width: 100%;
  }

  .filters .search-box {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 6px 10px;
    gap: 8px;
  }

  .filters .search-box input {
    border: none;
    outline: none;
    flex: 1;
    font-size: 13px;
    color: var(--text);
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}

/* === WEBSITE GRID / CARDS === */
@media (max-width: 992px) {
  .website-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
}

@media (max-width: 600px) {
  .website-grid {
    grid-template-columns: 1fr;
  }

  .website-card {
    padding: 1rem;
  }

  .website-card-header h4 {
    font-size: 1rem;
  }

  .page-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .page-stats {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .website-card-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .btn-copy {
    width: 100%;
    justify-content: center;
  }
}

/* === PAGINATION === */
@media (max-width: 600px) {
  .pagination {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .pagination-buttons {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }
}

/* === SPACING OPTIMIZATION === */
@media (max-width: 480px) {
  .booking-pages {
    padding: 0 0.8rem;
  }

  .kpi-card h4 {
    font-size: 0.9rem;
  }

  .kpi-card .value {
    font-size: 1.2rem;
  }

  .filters {
    gap: 8px;
  }

  .btn-primary {
    padding: 8px 12px;
    font-size: 13px;
  }
}

/* === Loader Overlay === */
.loader-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 5000;
  text-align: center;
  animation: fadeIn 0.3s ease-out;
}

.loader-overlay p {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin-top: 10px;
  letter-spacing: 0.3px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* === Placeholder / Empty State === */
.placeholder-box {
  background: var(--card);
  border: 1px dashed var(--border);
  border-radius: var(--radius);
  padding: 2.5rem 1rem;
  text-align: center;
  color: var(--muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 1rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.placeholder-box i {
  font-size: 2.5rem;
  color: var(--primary);
  opacity: 0.8;
}

.placeholder-box p {
  font-size: 14px;
  color: var(--text);
  font-weight: 500;
}

/* === Hover Interactions === */
.website-card {
  transition: all 0.25s ease;
}
.website-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}

/* === Dropdown Animation === */
.card-menu-dropdown {
  opacity: 0;
  transform: translateY(-5px);
  pointer-events: none;
  transition: all 0.25s ease;
}
.card-menu:hover .card-menu-dropdown {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

/* === Responsive Fine-Tuning === */
@media (max-width: 768px) {
  .placeholder-box {
    padding: 1.8rem 0.8rem;
  }

  .placeholder-box i {
    font-size: 2rem;
  }

  .placeholder-box p {
    font-size: 13px;
  }

  .loader-overlay p {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .spinner {
    width: 32px;
    height: 32px;
    border-width: 3px;
  }

  .placeholder-box {
    margin-top: 0.8rem;
  }

  .placeholder-box i {
    font-size: 1.8rem;
  }
}

/* === Fade In Animation (Reinforced) === */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

</style>
